import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

type AnnouncementType = 'info' | 'warning' | 'success' | 'maintenance';

interface Announcement {
  id: string;
  type: AnnouncementType;
  title: string;
  message: string;
  dismissible?: boolean;
  link?: {
    text: string;
    href: string;
  };
}

interface AnnouncementCardProps {
  announcements: Announcement[];
}

const STORAGE_KEY = 'dismissed-announcements';

function getDismissedAnnouncements(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function dismissAnnouncement(id: string): void {
  const dismissed = getDismissedAnnouncements();
  if (!dismissed.includes(id)) {
    dismissed.push(id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
  }
}

const typeStyles: Record<AnnouncementType, { container: string; icon: string }> = {
  info: {
    container:
      'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-200',
    icon: 'text-blue-500',
  },
  warning: {
    container:
      'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200',
    icon: 'text-amber-500',
  },
  success: {
    container:
      'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200',
    icon: 'text-green-500',
  },
  maintenance: {
    container:
      'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-700 text-orange-800 dark:text-orange-200',
    icon: 'text-orange-500',
  },
};

function AnnouncementItem({
  announcement,
  onDismiss,
}: {
  announcement: Announcement;
  onDismiss: (id: string) => void;
}) {
  const styles = typeStyles[announcement.type];
  const dismissible = announcement.dismissible !== false;

  return (
    <div
      className={`rounded-lg border p-4 ${styles.container}`}
      role="alert"
      data-testid={`announcement-${announcement.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium">{announcement.title}</p>
          <p className="mt-1 text-sm opacity-90">
            {announcement.message}
            {announcement.link && (
              <>
                {' '}
                <a
                  href={announcement.link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline underline-offset-2 hover:opacity-80"
                >
                  {announcement.link.text}
                </a>
              </>
            )}
          </p>
        </div>
        {dismissible && (
          <button
            onClick={() => onDismiss(announcement.id)}
            className="flex-shrink-0 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            aria-label="Dismiss announcement"
            data-testid={`dismiss-announcement-${announcement.id}`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export function AnnouncementCard({ announcements }: AnnouncementCardProps) {
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    setDismissed(getDismissedAnnouncements());
  }, []);

  const visibleAnnouncements = announcements.filter((a) => !dismissed.includes(a.id));

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  const handleDismiss = (id: string) => {
    dismissAnnouncement(id);
    setDismissed((prev) => [...prev, id]);
  };

  return (
    <div className="space-y-3 mb-6" data-testid="announcement-card">
      {visibleAnnouncements.map((announcement) => (
        <AnnouncementItem
          key={announcement.id}
          announcement={announcement}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
}

// Export types for consumers
export type { Announcement, AnnouncementType };
